import React, { useState, useEffect, useCallback } from 'react';
import axios, { API_URL } from '../config/api';
import { 
  MdFolder, MdFolderOpen, MdLabel, MdAdd, MdEdit, MdAutoAwesome, MdDelete,
  MdChevronRight, MdExpandMore, MdArrowForward, MdSearch, MdSync,
  MdHourglassEmpty, MdCheckCircle, MdCancel, MdPerson
} from 'react-icons/md';

export default function AdminTags() {
  const [hierarchy, setHierarchy] = useState([]);
  const [flatTags, setFlatTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoGrouping, setAutoGrouping] = useState(false);
  const [expandedParents, setExpandedParents] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [createModal, setCreateModal] = useState(null);
  const [reassignModal, setReassignModal] = useState(null);

  // Moderation state
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingBusyId, setPendingBusyId] = useState(null);
  const [migrationRequired, setMigrationRequired] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);

  const token = localStorage.getItem('token');

  const loadHierarchy = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tags/hierarchy`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHierarchy(res.data);
    } catch (err) {
      console.error('Error loading hierarchy:', err);
    }
  }, [token]);

  const loadFlatTags = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlatTags(res.data);
    } catch (err) {
      console.error('Error loading tags:', err);
    }
  }, [token]);

  const loadPending = useCallback(async () => {
    try {
      setPendingLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/tags/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPending(res.data?.items || []);
      setMigrationRequired(Boolean(res.data?.migration_required));
    } catch (err) {
      console.error('Error loading pending tags:', err);
      setPending([]);
    } finally {
      setPendingLoading(false);
    }
  }, [token]);

  useEffect(() => {
    Promise.all([loadHierarchy(), loadFlatTags(), loadPending()]).then(() => setLoading(false));
  }, [loadHierarchy, loadFlatTags, loadPending]);

  const toggleParent = (tagId) => {
    setExpandedParents(prev => ({ ...prev, [tagId]: !prev[tagId] }));
  };

  const handleAutoGroup = async () => {
    if (!window.confirm('This will use AI to automatically organize all ungrouped tags into categories. Continue?')) return;
    setAutoGrouping(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/tags/auto-group`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(res.data.message);
      await Promise.all([loadHierarchy(), loadFlatTags()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Auto-grouping failed');
    } finally {
      setAutoGrouping(false);
    }
  };

  const handleCreateTag = async (data) => {
    try {
      await axios.post(`${API_URL}/api/admin/tags`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreateModal(null);
      await Promise.all([loadHierarchy(), loadFlatTags(), loadPending()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create tag');
    }
  };

  const handleUpdateTag = async (tagId, data) => {
    try {
      await axios.put(`${API_URL}/api/admin/tags/${tagId}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditModal(null);
      await Promise.all([loadHierarchy(), loadFlatTags(), loadPending()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update tag');
    }
  };

  const handleReassign = async (tagId, newParentId) => {
    try {
      await axios.put(`${API_URL}/api/admin/tags/${tagId}/reassign`, 
        { parent_tag_id: newParentId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReassignModal(null);
      await Promise.all([loadHierarchy(), loadFlatTags(), loadPending()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reassign tag');
    }
  };

  const handleDeleteTag = async (tagId, tagName) => {
    if (!window.confirm(`Delete "${tagName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/tags/${tagId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await Promise.all([loadHierarchy(), loadFlatTags(), loadPending()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete tag');
    }
  };

  const handleApprove = async (tag) => {
    setPendingBusyId(tag.tag_id);
    try {
      await axios.post(`${API_URL}/api/admin/tags/${tag.tag_id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await Promise.all([loadHierarchy(), loadFlatTags(), loadPending()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve tag');
    } finally {
      setPendingBusyId(null);
    }
  };

  const handleReject = async (tag, reason) => {
    setPendingBusyId(tag.tag_id);
    try {
      await axios.post(`${API_URL}/api/admin/tags/${tag.tag_id}/reject`,
        { reason: reason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRejectModal(null);
      await Promise.all([loadHierarchy(), loadFlatTags(), loadPending()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reject tag');
    } finally {
      setPendingBusyId(null);
    }
  };

  // Filter hierarchy based on search
  const filteredHierarchy = searchQuery
    ? hierarchy.map(parent => ({
        ...parent,
        children: parent.children.filter(c => 
          c.name.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter(parent => 
        parent.name.toLowerCase().includes(searchQuery.toLowerCase()) || parent.children.length > 0
      )
    : hierarchy;

  const ungroupedCount = flatTags.filter(t => !t.parentTagId).length; // eslint-disable-line no-unused-vars
  const parentTags = flatTags.filter(t => !t.parentTagId && hierarchy.some(h => h.tagId === t.tagId)); // eslint-disable-line no-unused-vars

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <MdSync className="text-primary text-2xl animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-tags-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold font-rubik text-gray-900">Tag Hierarchy</h2>
          <p className="text-sm text-gray-500 mt-1">
            {hierarchy.length} categories &middot; {flatTags.length} total tags
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleAutoGroup}
            disabled={autoGrouping}
            className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            data-testid="auto-group-btn"
          >
            {autoGrouping ? <MdSync className="animate-spin" /> : <MdAutoAwesome />}
            {autoGrouping ? 'Grouping...' : 'AI Auto-Group'}
          </button>
          <button
            onClick={() => setCreateModal({ type: 'parent' })}
            className="flex items-center gap-2 px-3 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-medium transition-colors"
            data-testid="create-parent-tag-btn"
          >
            <MdAdd /> New Category
          </button>
          <button
            onClick={() => setCreateModal({ type: 'child' })}
            className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg text-sm font-medium transition-colors"
            data-testid="create-child-tag-btn"
          >
            <MdAdd /> New Tag
          </button>
        </div>
      </div>

      {/* Pending Moderation Section */}
      <PendingTagsSection
        pending={pending}
        loading={pendingLoading}
        busyId={pendingBusyId}
        migrationRequired={migrationRequired}
        onApprove={handleApprove}
        onReject={(tag) => setRejectModal(tag)}
      />

      {/* Search */}
      <div className="relative">
        <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
        <input
          type="text"
          placeholder="Search tags..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          data-testid="tag-search-input"
        />
      </div>

      {/* Hierarchy */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="tag-hierarchy-list">
        {filteredHierarchy.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchQuery ? 'No tags match your search.' : 'No tags found. Use "AI Auto-Group" to organize existing tags or create new categories.'}
          </div>
        ) : (
          filteredHierarchy.map(parent => (
            <div key={parent.tagId} className="border-b border-gray-100 last:border-0">
              {/* Parent row */}
              <div 
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                data-testid={`parent-tag-${parent.tagId}`}
              >
                <button 
                  onClick={() => toggleParent(parent.tagId)}
                  className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600"
                >
                  {expandedParents[parent.tagId] ? <MdExpandMore /> : <MdChevronRight />}
                </button>
                {expandedParents[parent.tagId] ? (
                  <MdFolderOpen className="text-lg" style={{ color: parent.color }} />
                ) : (
                  <MdFolder className="text-lg" style={{ color: parent.color }} />
                )}
                <span 
                  className="font-semibold text-sm text-gray-800 flex-1 cursor-pointer"
                  onClick={() => toggleParent(parent.tagId)}
                >
                  {parent.name}
                </span>
                <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
                  {parent.childCount} tags
                </span>
                <div className="flex gap-1">
                  {parent.childCount === 0 && (
                    <button
                      onClick={() => setReassignModal(parent)}
                      className="p-1.5 text-gray-400 hover:text-orange-600 rounded transition-colors"
                      title="Move to another category"
                      data-testid={`reassign-parent-${parent.tagId}`}
                    >
                      <MdArrowForward className="text-sm" />
                    </button>
                  )}
                  <button
                    onClick={() => setEditModal(parent)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 rounded transition-colors"
                    title="Edit category"
                    data-testid={`edit-parent-${parent.tagId}`}
                  >
                    <MdEdit className="text-sm" />
                  </button>
                  <button
                    onClick={() => handleDeleteTag(parent.tagId, parent.name)}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                    title="Delete category"
                    data-testid={`delete-parent-${parent.tagId}`}
                  >
                    <MdDelete className="text-sm" />
                  </button>
                </div>
              </div>

              {/* Children */}
              {expandedParents[parent.tagId] && (
                <div className="bg-gray-50/50">
                  {parent.children.length === 0 ? (
                    <div className="pl-12 pr-4 py-2 text-xs text-gray-400 italic">No subtags in this category</div>
                  ) : (
                    parent.children.map(child => (
                      <div 
                        key={child.tagId}
                        className="flex items-center gap-3 pl-12 pr-4 py-2 hover:bg-gray-100 transition-colors"
                        data-testid={`child-tag-${child.tagId}`}
                      >
                        <MdLabel className="text-sm" style={{ color: child.color }} />
                        <span className="text-sm text-gray-700 flex-1">{child.name}</span>
                        <div 
                          className="w-3 h-3 rounded-full border border-gray-300" 
                          style={{ backgroundColor: child.color }}
                          title={child.color}
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={() => setReassignModal(child)}
                            className="p-1 text-gray-400 hover:text-orange-600 rounded transition-colors"
                            title="Move to different category"
                            data-testid={`reassign-tag-${child.tagId}`}
                          >
                            <MdArrowForward className="text-sm" />
                          </button>
                          <button
                            onClick={() => setEditModal(child)}
                            className="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors"
                            title="Edit tag"
                          >
                            <MdEdit className="text-sm" />
                          </button>
                          <button
                            onClick={() => handleDeleteTag(child.tagId, child.name)}
                            className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                            title="Delete tag"
                          >
                            <MdDelete className="text-sm" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Edit Modal */}
      {editModal && (
        <EditTagModal
          tag={editModal}
          onSave={(data) => handleUpdateTag(editModal.tagId, data)}
          onClose={() => setEditModal(null)}
        />
      )}

      {/* Create Modal */}
      {createModal && (
        <CreateTagModal
          type={createModal.type}
          parentTags={hierarchy}
          onSave={handleCreateTag}
          onClose={() => setCreateModal(null)}
        />
      )}

      {/* Reassign Modal */}
      {reassignModal && (
        <ReassignModal
          tag={reassignModal}
          parentTags={hierarchy}
          onSave={(newParentId) => handleReassign(reassignModal.tagId, newParentId)}
          onClose={() => setReassignModal(null)}
        />
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <RejectTagModal
          tag={rejectModal}
          busy={pendingBusyId === rejectModal.tag_id}
          onConfirm={(reason) => handleReject(rejectModal, reason)}
          onClose={() => setRejectModal(null)}
        />
      )}
    </div>
  );
}


/* ── Pending review section ─────────────────────────────────────── */
function PendingTagsSection({ pending, loading, busyId, migrationRequired, onApprove, onReject }) {
  if (loading) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 font-rubik" data-testid="admin-tags-pending-loading">
        Loading pending tags...
      </div>
    );
  }
  if (migrationRequired) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-900 font-rubik" data-testid="admin-tags-migration-required">
        <strong className="block mb-1">Tag moderation migration not applied yet.</strong>
        Apply <code className="bg-white px-1 py-0.5 rounded text-xs">/app/backend/migrations/add_tag_moderation_columns.sql</code> in the Supabase SQL editor to enable user-tag approval. Until then, new user-created tags will continue to be auto-approved.
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-amber-200 overflow-hidden" data-testid="admin-tags-pending-section">
      <div className="flex items-center justify-between px-4 py-3 bg-amber-50/60 border-b border-amber-200">
        <div className="flex items-center gap-2">
          <MdHourglassEmpty className="text-amber-600" />
          <h3 className="text-sm font-semibold font-rubik text-amber-900">Pending review</h3>
          <span className="text-xs bg-white border border-amber-300 text-amber-800 rounded-full px-2 py-0.5">
            {pending.length}
          </span>
        </div>
        <span className="text-[11px] text-amber-700 italic hidden sm:inline">User-suggested topics awaiting your decision</span>
      </div>
      {pending.length === 0 ? (
        <div className="p-6 text-sm text-gray-500 text-center" data-testid="admin-tags-pending-empty">
          Nothing to review right now. New user-suggested topics will appear here.
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {pending.map((t) => {
            const ago = t.created_at ? new Date(t.created_at).toLocaleString() : '';
            return (
              <li
                key={t.tag_id}
                className="flex items-center gap-3 px-4 py-3 hover:bg-amber-50/30 transition-colors"
                data-testid={`pending-tag-${t.tag_id}`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: t.color || '#6B7280' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 font-rubik truncate" data-testid={`pending-tag-name-${t.tag_id}`}>
                    {t.name}
                  </div>
                  <div className="text-[11px] text-gray-500 font-rubik flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <MdPerson className="text-gray-400" />
                      {t.created_by_username || 'Unknown user'}
                    </span>
                    <span>·</span>
                    <span>{ago}</span>
                    {t.pin_count > 0 && (
                      <>
                        <span>·</span>
                        <span>{t.pin_count} pin{t.pin_count === 1 ? '' : 's'}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onApprove(t)}
                    disabled={busyId === t.tag_id}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium font-rubik disabled:opacity-50"
                    data-testid={`approve-tag-${t.tag_id}`}
                  >
                    <MdCheckCircle className="text-sm" /> Approve
                  </button>
                  <button
                    onClick={() => onReject(t)}
                    disabled={busyId === t.tag_id}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white border border-rose-300 hover:bg-rose-50 text-rose-700 text-xs font-medium font-rubik disabled:opacity-50"
                    data-testid={`reject-tag-${t.tag_id}`}
                  >
                    <MdCancel className="text-sm" /> Reject
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


function RejectTagModal({ tag, busy, onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="reject-tag-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
        <h3 className="text-lg font-bold mb-2 font-rubik">Reject &ldquo;{tag.name}&rdquo;?</h3>
        <p className="text-sm text-gray-500 mb-4 font-rubik">
          This will unpin it from anyone who added it and notify the creator.
        </p>
        <label className="block text-xs font-medium text-gray-700 mb-1 font-rubik uppercase tracking-wide">
          Reason (optional — sent to the user)
        </label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          maxLength={280}
          rows={3}
          placeholder="e.g. Too narrow, duplicates 'Climate Change', not relevant to civic issues..."
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-100 focus:border-rose-400 outline-none font-rubik"
          data-testid="reject-reason-input"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-rubik disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason.trim())}
            disabled={busy}
            className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-medium font-rubik disabled:opacity-50"
            data-testid="confirm-reject-btn"
          >
            {busy ? 'Rejecting...' : 'Reject tag'}
          </button>
        </div>
      </div>
    </div>
  );
}


function EditTagModal({ tag, onSave, onClose }) {
  const [name, setName] = useState(tag.name);
  const [color, setColor] = useState(tag.color);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="edit-tag-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
        <h3 className="text-lg font-bold mb-4 font-rubik">Edit Tag</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              data-testid="edit-tag-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={color}
                onChange={e => setColor(e.target.value)}
                className="w-10 h-10 rounded cursor-pointer border-0"
                data-testid="edit-tag-color-input"
              />
              <input
                type="text"
                value={color}
                onChange={e => setColor(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button
            onClick={() => onSave({ name, color })}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90"
            data-testid="save-edit-tag-btn"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}


function CreateTagModal({ type, parentTags, onSave, onClose }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [parentId, setParentId] = useState('');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="create-tag-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
        <h3 className="text-lg font-bold mb-4 font-rubik">
          {type === 'parent' ? 'New Category' : 'New Tag'}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              placeholder={type === 'parent' ? 'e.g., Education' : 'e.g., School Choice'}
              data-testid="create-tag-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={color}
                onChange={e => setColor(e.target.value)}
                className="w-10 h-10 rounded cursor-pointer border-0"
              />
              <input
                type="text"
                value={color}
                onChange={e => setColor(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
              />
            </div>
          </div>
          {type === 'child' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent Category</label>
              <select
                value={parentId}
                onChange={e => setParentId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                data-testid="create-tag-parent-select"
              >
                <option value="">None (standalone)</option>
                {parentTags.map(p => (
                  <option key={p.tagId} value={p.tagId}>{p.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button
            onClick={() => onSave({ name, color, parent_tag_id: type === 'child' ? (parentId || null) : null })}
            disabled={!name.trim()}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
            data-testid="save-create-tag-btn"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}


function ReassignModal({ tag, parentTags, onSave, onClose }) {
  const [newParentId, setNewParentId] = useState(tag.parentTagId || '');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="reassign-tag-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
        <h3 className="text-lg font-bold mb-2 font-rubik">Move Tag</h3>
        <p className="text-sm text-gray-500 mb-4">
          Reassign <strong>{tag.name}</strong> to a different category
        </p>
        <select
          value={newParentId}
          onChange={e => setNewParentId(e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          data-testid="reassign-parent-select"
        >
          <option value="">No category (standalone)</option>
          {parentTags.filter(p => p.tagId !== tag.tagId).map(p => (
            <option key={p.tagId} value={p.tagId}>{p.name}</option>
          ))}
        </select>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button
            onClick={() => onSave(newParentId)}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700"
            data-testid="save-reassign-btn"
          >
            Move
          </button>
        </div>
      </div>
    </div>
  );
}
